import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { placeholder, lazyload } from '@cloudinary/react';
import { Text, TextVariant } from '@naf/text';
import { FeaturedBenefitStep } from '../../../../../../../../types/featuredBenefitBlockType';
import { useCloudinary } from '../../../../../../hooks/Cloudinary/useCloudinary';
import BlockContent from '../../../../../block-content/BlockContent';
import { useDocumentUrlWithNode } from '../../../../../../hooks/Documents/useDocumentUrl';
import * as S from './Step.styles';

export const Step: React.FC<{ step: FeaturedBenefitStep }> = ({ step }) => {
  const { getResponsiveImage } = useCloudinary();
  const { title, description, stepImage, illustration, link } = step;
  const target = useDocumentUrlWithNode(link?.data) || '';

  const cldImage = useMemo(
    () => getResponsiveImage(stepImage?.publicId, { format: illustration ? 'svg' : 'auto', quality: 'auto' }),
    [getResponsiveImage, stepImage?.publicId, illustration],
  );

  const renderImageWithLink = () => {
    if (link?.type === 'internalLink') {
      return (
        <Link to={target}>
          <S.StyledAdvancedImage
            alt={stepImage?.altText || ''}
            cldImg={cldImage}
            plugins={[lazyload({ rootMargin: '10px 20px 10px 30px', threshold: 0.25 }), placeholder({ mode: 'blur' })]}
          />
        </Link>
      );
    }

    if (link?.type === 'link') {
      return (
        <a href={step?.link?.data?.href} target={step?.link ? '_blank' : '_self'} rel="noopener noreferrer">
          <S.StyledAdvancedImage
            alt={stepImage?.altText || ''}
            cldImg={cldImage}
            plugins={[lazyload({ rootMargin: '10px 20px 10px 30px', threshold: 0.25 }), placeholder({ mode: 'blur' })]}
          />
        </a>
      );
    }

    return (
      <S.StyledAdvancedImage
        alt={stepImage?.altText || ''}
        cldImg={cldImage}
        plugins={[lazyload({ rootMargin: '10px 20px 10px 30px', threshold: 0.25 }), placeholder({ mode: 'blur' })]}
      />
    );
  };

  return (
    <S.StyledStep>
      <S.IconWrapper>{cldImage && renderImageWithLink()}</S.IconWrapper>
      <S.TextContent>
        <Text variant={TextVariant.Header2}>{title}</Text>
        {/* Ensure backwards compatability as we move from string to block content. */}
        {typeof description === 'string' ? (
          <Text variant={TextVariant.BodyText}>{description}</Text>
        ) : (
          <BlockContent value={description} />
        )}
      </S.TextContent>
    </S.StyledStep>
  );
};
